import { z } from 'zod';
import { HaendlerFelder, haendlerPflichtFehler, type HaendlerStatus, type Herkunft, type Verkaufsmodell } from './haendler';
import { RECHTSFORMEN, RECHTSFORM_CODES, registerNrNormalisieren } from './rechtsformen';
import { ustIdNormalisieren } from './einstellungen';
import { Passwort } from './auth';
import type { PortalRecht } from './portalTeam';

/**
 * Händlerportal unter /m/<kürzel>/portal – eigene Zugänge je Händler, getrennt vom Backoffice
 * (portal/api.php der PHP-Middleware).
 */

export const PORTAL_ROLLEN = { inhaber: 'Inhaber', buchhaltung: 'Buchhaltung', mitarbeiter: 'Mitarbeiter' } as const;
export type PortalRolle = keyof typeof PORTAL_ROLLEN;

/** Erklärungen bei der Registrierung (wie zustimmungen[] der PHP-Middleware) */
export const ZUSTIMMUNGEN = {
  vertrag_akzeptiert: 'Bitte akzeptieren Sie den Händlervertrag',
  unternehmer_bestaetigt: 'Bitte bestätigen Sie, dass Sie als Unternehmer handeln',
  datenschutz_gelesen: 'Bitte bestätigen Sie, dass Sie die Datenschutzerklärung zur Kenntnis genommen haben',
} as const;
export type Zustimmung = keyof typeof ZUSTIMMUNGEN;

export const RECHTSTEXT_DOKUMENTE = { haendlervertrag: 'Händlervertrag', agb: 'AGB für Käufer', datenschutz: 'Datenschutzerklärung' } as const;
export type RechtstextDokument = keyof typeof RECHTSTEXT_DOKUMENTE;

export interface Rechtstext {
  dokument: RechtstextDokument;
  titel: string;
  /** vollständiges HTML-Dokument mit eigenen Stilen – in der Oberfläche nur in einem abgeschotteten iframe zeigen */
  html: string;
  version: string;
  stand: string;
  sha256: string;
  /** eigene Fassung des Marktplatzes (sonst Vorlage von DXL) */
  angepasst: boolean;
  /** Bezeichnungen der Platzhalter ohne Wert – im Text als „[… fehlt]“ markiert */
  fehlend: string[];
}

/** Gilt in Deutschland: fünfstellige PLZ vor dem Ort (wie die PHP-Middleware bei der Registrierung) */
const deutschland = (land: string) => /^(deutschland|germany|de)$/i.test(land.trim());

/** Pflichtangaben der Adresse – eigene Funktion, damit die Oberfläche sie auch zeigt, wenn ein anderes Feld ungültig ist */
export function portalAdressFehler(h: { strasse?: string; ort?: string; land?: string }): { feld: string; text: string }[] {
  const fehler: { feld: string; text: string }[] = [];
  const strasse = (h.strasse ?? '').trim();
  const ort = (h.ort ?? '').trim();
  if (!strasse || !/\d/.test(strasse)) fehler.push({ feld: 'strasse', text: 'Bitte Straße und Hausnummer angeben' });
  if (!ort) fehler.push({ feld: 'ort', text: 'Bitte PLZ und Ort angeben' });
  else if (deutschland(h.land ?? '') && !/^\d{5}\s+\S/.test(ort)) fehler.push({ feld: 'ort', text: 'Bitte eine gültige deutsche PLZ (5 Ziffern) und den Ort angeben' });
  if (!(h.land ?? '').trim()) fehler.push({ feld: 'land', text: 'Bitte Land angeben' });
  return fehler;
}

/** Stammdaten, die der Händler selbst pflegt: ohne E-Mail (Anmeldung), Verkaufsmodell und Provision (legt der Marktplatz fest) */
const PortalFelder = HaendlerFelder.omit({ email: true, modell: true, provision: true }).extend({
  rechtsform: z.enum(RECHTSFORM_CODES, { errorMap: () => ({ message: 'Bitte Rechtsform wählen' }) }),
  ansprechpartner: z.string().trim().min(2, 'Bitte Ansprechpartner angeben').max(120, 'Höchstens 120 Zeichen'),
});

type Basis = z.output<typeof PortalFelder>;
const registerVereinheitlichen = <T extends Basis>(h: T): T => ({ ...h, registerNr: registerNrNormalisieren(h.registerNr, RECHTSFORMEN[h.rechtsform].reg) });
const regelnPruefen = (h: Basis, ctx: z.RefinementCtx) => {
  for (const f of [...haendlerPflichtFehler(h), ...portalAdressFehler(h)]) ctx.addIssue({ code: 'custom', path: [f.feld], message: f.text });
};

export const PortalStammdatenSchema = PortalFelder.transform(registerVereinheitlichen).superRefine(regelnPruefen);
export type PortalStammdatenEingabe = z.input<typeof PortalFelder>;
export type PortalStammdaten = z.output<typeof PortalStammdatenSchema>;

/**
 * Registrierung: Stammdaten, Zugang und Zustimmungen. Bot-Schutz über ein signiertes Formular-Token
 * (Mindestdauer) und ein unsichtbares Lockfeld „firmenFax“.
 */
export const PortalRegistrierungSchema = PortalFelder.extend({
  email: z.string().trim().toLowerCase().email('Bitte gültige E-Mail angeben').max(190),
  passwort: Passwort,
  zustimmungen: z.array(z.string()).max(10).default([]),
  vertragSha256: z.string().max(64).default(''),
  formular: z.string().max(100).default(''),
  firmenFax: z.string().max(200).default(''),
})
  .transform(registerVereinheitlichen)
  .superRefine(regelnPruefen)
  .superRefine((h, ctx) => {
    for (const [k, text] of Object.entries(ZUSTIMMUNGEN)) {
      if (!h.zustimmungen.includes(k)) ctx.addIssue({ code: 'custom', path: [k], message: text });
    }
  });
export type PortalRegistrierungEingabe = z.input<typeof PortalFelder> & {
  email: string;
  passwort: string;
  zustimmungen: Zustimmung[];
  vertragSha256: string;
  formular: string;
  firmenFax?: string;
};

/**
 * Händlerangaben im Vertrag – dieselbe Aufbereitung für die Anzeige und das Absenden,
 * damit der SHA-256 des gezeigten Texts übereinstimmt (reg_haendler der PHP-Middleware).
 */
export function vertragsAngaben(h: Partial<Record<'firma' | 'rechtsform' | 'registerGericht' | 'registerNr' | 'ustId' | 'vertretung' | 'strasse' | 'ort' | 'land' | 'email', string>>) {
  const t = (v?: string) => (v ?? '').replace(/\s+/g, ' ').trim();
  const reg = h.rechtsform && h.rechtsform in RECHTSFORMEN ? RECHTSFORMEN[h.rechtsform as keyof typeof RECHTSFORMEN].reg : null;
  return {
    firma: t(h.firma),
    strasse: t(h.strasse),
    ort: t(h.ort),
    land: t(h.land),
    register: reg === null ? '' : `${t(h.registerGericht)} ${registerNrNormalisieren(t(h.registerNr), reg)}`.trim(),
    ustId: ustIdNormalisieren(t(h.ustId)),
    vertretung: t(h.vertretung),
    email: t(h.email).toLowerCase(),
  };
}
export type VertragsAngaben = ReturnType<typeof vertragsAngaben>;

export const PortalAnmeldungSchema = z.object({
  email: z.string().trim().toLowerCase().email('Bitte gültige E-Mail angeben'),
  passwort: z.string().min(1, 'Bitte Passwort eingeben').max(200),
  angemeldetBleiben: z.boolean().default(false),
});

export interface PortalRegistrierungInfo {
  marktplatz: string;
  /** signiertes Zeit-Token gegen Bots – beim Absenden mitschicken */
  formular: string;
  /** Mindestdauer bis zum Absenden in Sekunden */
  mindestSekunden: number;
}

export interface PortalMe {
  stufe: 'zweiter_faktor' | 'voll';
  csrf: string;
  zugang: { id: string; name: string; email: string; rolle: PortalRolle; zweiFaktorAktiv: boolean };
  haendler: { id: string; firma: string; status: HaendlerStatus; statusGrund: string };
  mandant: { kuerzel: string; name: string };
  /** Bereiche, die die Rolle freischaltet (PORTAL_ROLLEN_RECHTE) */
  rechte: PortalRecht[];
  /** Nutzer des Marktplatz-Kontos, der das Portal als dieser Händler sieht – sonst null */
  ansicht: { name: string } | null;
  /** neue Version des Händlervertrags, der noch nicht zugestimmt wurde */
  vertragZustimmen: boolean;
  /**
   * Nur Retail Media (RM-8): Zugang einer eingeladenen Marke oder Shop ohne Modul Marktplatz – das Portal zeigt
   * Werbung, Konto und Team, aber keine Produkte, Bestellungen, Auszahlungen, Anbindungen oder Verträge.
   */
  nurWerbung: boolean;
}

export interface PortalKonto extends PortalStammdaten {
  email: string;
  status: HaendlerStatus;
  statusGrund: string;
  modell: Verkaufsmodell;
  /** null = Standardsatz des Marktplatzes */
  provision: number | null;
  code: string | null;
  shop: string | null;
  shopVerbundenAm: string | null;
  herkunft: Herkunft;
  erstelltAm: string;
  zustimmungen: { dokument: RechtstextDokument; version: string; stand: string; zeitpunkt: string }[];
}

export interface PortalCode {
  code: string;
  shop: string | null;
  shopVerbundenAm: string | null;
}
